import { FormRowStatus } from 'atoms/create-resource-atom';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { ComboBox, ComboBoxProps, Layer } from '@carbon/react';

import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { ApiQueryResource, ApiQueryResponse } from 'types/api-responses';
import { shouldSkipSearch } from 'utils/forms';

interface FormProps<Response, Params extends SearchParams> extends Omit<
  ComboBoxProps<Response>,
  'items' | 'selectedItem'
> {
  selectedItem: Response | undefined;
  selectedItemToString: (response: Response | null | undefined) => string;
  queryResource: ApiQueryResource;
  status: FormRowStatus;
  additionalPayload?: Params;
  light?: boolean;
  disabled?: boolean;
  className?: string;
  /** Run a blank search the first time the field is focused, so it opens with results. */
  searchOnFocus?: boolean;
}

const AutoComplete = <
  Response extends ApiQueryResponse,
  Params extends SearchParams = SearchParams,
>({
  selectedItem,
  selectedItemToString,
  onChange,
  additionalPayload,
  className,
  titleText,
  queryResource,
  status,
  light = false,
  disabled = false,
  searchOnFocus = false,
  ...rest
}: FormProps<Response, Params>) => {
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () => searchAtomPair<Response, Params>(queryResource),
    [queryResource]
  );

  const [query, setPayload] = useAtom(searchAtom);
  const resetSearchData = useSetAtom(resetSearchDataAtom);

  const firstRender = useRef(true);
  useEffect(() => {
    firstRender.current = false;
  }, []);

  const debouncedSetPayload = useMemo(
    () => debounce(setPayload, 400),
    [setPayload]
  );

  // First focus only, not mount: each instance owns its search atoms, and a later focus follows a blur that already
  // searched blank.
  const primedRef = useRef(false);
  const primeSearch = () => {
    if (!searchOnFocus || primedRef.current) return;
    primedRef.current = true;
    if (query?.data) return;
    setPayload(
      (c) => ({ ...c, text: '', pageSize: 10, ...additionalPayload }) as Params
    );
  };

  const comboBox = (
    <ComboBox<Response>
      titleText={titleText}
      items={query?.data?.results || []}
      className={className}
      itemToString={selectedItemToString}
      selectedItem={selectedItem || null}
      onChange={({ selectedItem }) => {
        onChange?.({ selectedItem: selectedItem ?? undefined });
      }}
      onInputChange={(text) => {
        if (shouldSkipSearch(firstRender.current, status)) return;
        resetSearchData(undefined);
        debouncedSetPayload(
          (c) =>
            ({
              ...c,
              text,
              pageSize: 10,
              ...additionalPayload,
            }) as Params
        );
      }}
      disabled={disabled}
      {...rest}
      onFocus={(event) => {
        primeSearch();
        rest.onFocus?.(event);
      }}
    />
  );

  return light ? <Layer>{comboBox}</Layer> : comboBox;
};

export { AutoComplete };
